/**
 * storefrontNavInk -- contrast-safe ink + underline for the storefront
 * category nav's ACTIVE item.
 *
 * Why this exists: the active nav item was painted with the store's
 * `brand_color` applied RAW. brand_color is distributor-chosen and
 * unconstrained, so a store whose brand is white or near-white rendered the
 * active item white-on-white against the nav's white bar -- invisible. The
 * active item is the only affordance telling a shopper which category they
 * are on, so that is a navigation failure, not a cosmetic one.
 *
 * Relationship to `storefrontBrandAccent.js`: that module solves the same
 * class of problem for the Home page's welcome card, but it derives against
 * the LIGHT CARD surface (#f5f8fa) and returns a single heading ink. The nav
 * bar is white (#fff) and needs TWO values at TWO different WCAG bars, so
 * the surface and the bars are parameters here rather than constants. Both
 * modules share the same underlying WCAG math from `utils/luminance` and the
 * same "darken toward the brand hue, don't jump to a generic gray" strategy,
 * so a clamped color still reads as this store's color.
 *
 * Two bars, deliberately different (WCAG 2.x):
 *   - TEXT ink: 4.5:1 (AA, 1.4.3). The item label is text.
 *   - UNDERLINE: 3:1 (1.4.11 non-text contrast). The underline is a
 *     graphical affordance, so holding it to the text bar would darken
 *     brand colors that are perfectly legible AS A BAR. This keeps more
 *     stores rendering their real brand color on the underline.
 */
import { hexToRgb, contrastRatio, relativeLuminance } from "@/utils/luminance";

// WCAG AA for normal text.
export const TEXT_MIN_RATIO = 4.5;
// WCAG 1.4.11 non-text contrast (graphical objects / UI components).
export const NON_TEXT_MIN_RATIO = 3;

// Safe inks for the terminal fallback, used only when scaling the brand hue
// fails to clear the bar. DEFAULT_INK matches --tf-fg-1; the white
// counterpart covers a dark surface, where darkening moves a color TOWARD
// the background instead of away from it.
const DEFAULT_INK = "#1c1c1c";
const DEFAULT_INK_ON_DARK = "#ffffff";

function toHex2(n) {
  return Math.max(0, Math.min(255, Math.round(n)))
    .toString(16)
    .padStart(2, "0");
}

// Scale an {r,g,b} triple toward black by `factor` (0 = unchanged, 1 = black).
function darken(rgb, factor) {
  return {
    r: rgb.r * (1 - factor),
    g: rgb.g * (1 - factor),
    b: rgb.b * (1 - factor),
  };
}

// Scale an {r,g,b} triple toward white by `factor` (0 = unchanged, 1 = white).
function lighten(rgb, factor) {
  return {
    r: rgb.r + (255 - rgb.r) * factor,
    g: rgb.g + (255 - rgb.g) * factor,
    b: rgb.b + (255 - rgb.b) * factor,
  };
}

function rgbToHex(rgb) {
  return `#${toHex2(rgb.r)}${toHex2(rgb.g)}${toHex2(rgb.b)}`;
}

/**
 * Clamp `hex` so it clears `minRatio` against `surfaceHex`.
 *
 * A color that already clears the bar is returned VERBATIM -- a store with a
 * legitimate dark brand color keeps its own branding, which is why this is a
 * guard and not a blanket darkening. Otherwise the brand color is scaled in
 * fixed steps until it clears, preserving the hue.
 *
 * Direction is chosen from the SURFACE, not assumed: on a light surface
 * (today's nav bar) the color darkens; on a dark surface it lightens, since
 * darkening there would move it toward the background rather than away.
 *
 * @param {string} hex          Candidate color (any form hexToRgb accepts).
 * @param {string} surfaceHex   Background it will be drawn against.
 * @param {number} minRatio     Contrast floor to clear.
 * @returns {string|null} A color clearing the floor, or null for invalid input.
 */
export function clampToContrast(hex, surfaceHex, minRatio) {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;

  const ratio = contrastRatio(hex, surfaceHex);
  if (ratio !== null && ratio >= minRatio) {
    return hex;
  }

  // Move AWAY from the surface: a light background wants darker ink, a dark
  // background wants lighter ink.
  const surfaceLuminance = relativeLuminance(surfaceHex);
  const goDarker = surfaceLuminance === null || surfaceLuminance > 0.5;
  const scale = goDarker ? darken : lighten;

  const steps = [0.25, 0.45, 0.6, 0.75, 0.9, 1];
  for (const factor of steps) {
    const candidate = rgbToHex(scale(rgb, factor));
    const candidateRatio = contrastRatio(candidate, surfaceHex);
    if (candidateRatio !== null && candidateRatio >= minRatio) {
      return candidate;
    }
  }
  return goDarker ? DEFAULT_INK : DEFAULT_INK_ON_DARK;
}

/**
 * Build the active nav item's inline style: brand color where it is legible,
 * a darkened-toward-the-brand-hue substitute where it is not.
 *
 * Text and underline are clamped INDEPENDENTLY at their own WCAG bars, so a
 * mid-tone brand color can legitimately end up with a clamped label and a
 * verbatim brand underline.
 *
 * @param {string} brandColor   The store's brand color (hex).
 * @param {string} surfaceHex   The nav bar background the item sits on.
 * @returns {{color: string, borderBottomColor: string}} Always legible; falls
 *   back to DEFAULT_INK for a missing/invalid brand color rather than
 *   emitting a broken value.
 */
export function storefrontNavActiveStyle(brandColor, surfaceHex) {
  const ink = clampToContrast(brandColor, surfaceHex, TEXT_MIN_RATIO);
  const underline = clampToContrast(
    brandColor,
    surfaceHex,
    NON_TEXT_MIN_RATIO
  );

  return {
    color: ink || DEFAULT_INK,
    borderBottomColor: underline || DEFAULT_INK,
  };
}

export default storefrontNavActiveStyle;
